import { Check, Copy } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";

import { copyText } from "@/lib/clipboard";
import { cn } from "@/lib/utils/cn";

// One-tap copy for the values the operator pastes into a trade: an arbitrage's amounts and token.
// [[spec-copyable-ui-values-and-end-to-end-coverage-of-the-web-flows]] R1

const FEEDBACK_MS = 1500;

interface CopyValueProps {
    value: string | number;
    /** Names the value in the button's accessible name and the toast. */
    label: string;
    /** What to show instead of the raw value, e.g. a badge; the raw value is what copies. */
    children?: ReactNode;
    className?: string;
}

/** The value itself is the button, with a small copy icon as the affordance. */
export function CopyValue({ value, label, children, className }: CopyValueProps) {
    const text = String(value);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), FEEDBACK_MS);
        return () => clearTimeout(timer);
    }, [copied]);

    const copy = async () => {
        // One toast id, so rapid copies replace each other instead of stacking up.
        if (await copyText(text)) {
            setCopied(true);
            // Short-lived: the operator often copies several values in a row.
            toast.success(`Copied ${label}`, { id: "copy", description: text, duration: FEEDBACK_MS });
        } else {
            toast.error(`Could not copy ${label}`, { id: "copy", description: "Select the text and copy it by hand." });
        }
    };

    const Icon = copied ? Check : Copy;
    return (
        <button
            type="button"
            onClick={copy}
            aria-label={`Copy ${label}`}
            title={text}
            data-field={label}
            data-copy-value={text}
            className={cn(
                "group inline-flex max-w-full cursor-copy items-center gap-1 rounded-sm text-left align-middle focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                className,
            )}
        >
            <span className="min-w-0 break-all">{children ?? text}</span>
            <Icon
                aria-hidden
                className={cn(
                    "size-3.5 shrink-0 transition-opacity",
                    copied
                        ? "text-success opacity-100"
                        : "text-muted-foreground opacity-50 group-hover:opacity-100 group-focus-visible:opacity-100",
                )}
            />
        </button>
    );
}
